import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useOAuth } from '../../context/OAuthContext';
import './ResponsiveNavbar.css';

const ADMIN_PANEL_PERMISSIONS = [
  'admin.access',
  'products.view',
  'categories.view',
  'textures.view',
  'images.view',
  'galleries.view',
  'orders.view',
  'users.view',
  'roles.view',
  'admin.settings',
  'jobs.view',
  'applications.view',
  'news.view',
  'publications.create',
  'publications.edit',
  'publications.delete',
  'publications.control',
  'staff_roster.view',
  'staff_roster.create',
  'staff_roster.edit',
  'staff_roster.archive',
  'staff_roster.delete',
  'staff_roster.assign_compensation',
  'staff_roster.manage_structure',
  'staff_roster.review_pending',
  'staff_roster.manage_approvals',
  'staff_roster.view_summaries',
  'staff_roster.view_history'
];

const ResponsiveNavbar = ({ logo, logoLink, scrolled }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useOAuth();

  const accountIcon = (
    <svg
      className="nav-account-icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 21a8 8 0 0 0-16 0" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );

  const adminIcon = (
    <svg
      className="nav-admin-icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 13c0 5-3.5 7.5-8 9-4.5-1.5-8-4-8-9V5l8-3 8 3z" />
    </svg>
  );

  const roleId = currentUser?.roleId || currentUser?.adminUser?.roleId;
  const userPermissions = currentUser?.permissions || currentUser?.adminUser?.permissions || [];
  const hasAdminPanelAccess = Boolean(
    currentUser && (
      roleId === 'admin' ||
      roleId === 'administrator' ||
      (Array.isArray(userPermissions) && userPermissions.some(permission => ADMIN_PANEL_PERMISSIONS.includes(permission)))
    )
  );

  const siteMenuItems = [
    { type: 'link', label: 'Home', to: '/#home' },
    { type: 'link', label: 'News', to: '/news' },
    {
      type: 'link',
      label: 'Publications',
      to: '/publications',
      active: location.pathname === '/publications' || location.pathname.startsWith('/publication/')
    },
    {
      type: 'dropdown',
      label: 'Departments',
      items: [
        { type: 'link', label: 'Studios', to: '/studios' },
        { type: 'link', label: 'Vague', to: '/#vague' },
        { type: 'link', label: 'Gallery', to: '/studios#gallery' }
      ]
    },
    { type: 'link', label: 'Careers', to: '/careers' },
    {
      type: 'dropdown',
      label: 'About Us',
      items: [
        { type: 'link', label: 'About', to: '/#about' },
        { type: 'link', label: 'Team', to: '/team' },
        { type: 'link', label: 'Partners', to: '/partners' }
      ]
    },
    { type: 'link', label: 'Shop', to: '/shop' },
    ...(hasAdminPanelAccess ? [{
      type: 'link',
      label: 'Admin Panel',
      to: '/admin',
      icon: adminIcon,
      iconOnly: true,
      className: 'nav-admin-item'
    }] : []),
    { type: 'link', label: 'Portal', to: 'https://gtaw.link/veloura', external: true, className: 'nav-portal-item' },
    { type: 'link', label: 'Account', to: '/account', icon: accountIcon, iconOnly: true, className: 'nav-account-item' }
  ];

  const normalizedMenuItems = siteMenuItems;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.navbar') && !event.target.closest('.mobile-menu')) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const scrollToHash = (hash) => {
    if (!hash) return;
    const element = document.querySelector(hash);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleHashNavigation = (event, to) => {
    const [path, hashValue] = to.split('#');
    const targetPath = path || '/';
    const hash = hashValue ? `#${hashValue}` : '';

    if (!hash) return false;

    event.preventDefault();
    closeMenu();

    if (location.pathname !== targetPath) {
      navigate(targetPath);
      window.setTimeout(() => scrollToHash(hash), 120);
    } else {
      scrollToHash(hash);
    }

    return true;
  };

  const renderMenuLink = (item, mobile = false) => {
    const linkStyle = mobile ? {
      color: '#ffffff',
      WebkitTextFillColor: '#ffffff',
      display: 'block',
      opacity: 1,
      visibility: 'visible',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      backgroundClip: 'border-box',
      WebkitBackgroundClip: 'border-box',
      fontSize: '1rem',
      fontWeight: '500',
      textShadow: 'none',
      mixBlendMode: 'normal'
    } : undefined;

    const linkClassName = [
      item.active || location.pathname === item.to ? 'active' : '',
      item.iconOnly ? 'nav-icon-link' : '',
      item.className || ''
    ].filter(Boolean).join(' ');

    const content = item.iconOnly ? (
      <>
        {item.icon}
        <span className="sr-only">{item.label}</span>
      </>
    ) : item.label;

    if (item.external) {
      return (
        <a
          href={item.to}
          className={linkClassName}
          style={linkStyle}
          onClick={mobile ? closeMenu : undefined}
        >
          {content}
        </a>
      );
    }

    if (item.to && (item.to.startsWith('#') || item.to.includes('#'))) {
      return (
        <a
          href={item.to}
          className={linkClassName}
          style={linkStyle}
          title={item.iconOnly ? item.label : undefined}
          aria-label={item.iconOnly ? item.label : undefined}
          onClick={(e) => handleHashNavigation(e, item.to)}
        >
          {content}
        </a>
      );
    }

    return (
      <Link
        to={item.to}
        onClick={mobile ? closeMenu : undefined}
        className={linkClassName}
        style={linkStyle}
        title={item.iconOnly ? item.label : undefined}
        aria-label={item.iconOnly ? item.label : undefined}
      >
        {content}
      </Link>
    );
  };

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''} ${isMenuOpen ? 'menu-open' : ''}`}>
      <div className="nav-container">
        <div className="nav-logo">
          {logoLink ? (
            <Link to={logoLink} onClick={closeMenu}>
              {logo}
            </Link>
          ) : (
            logo
          )}
        </div>
        {}
        <ul className="nav-menu">
            {normalizedMenuItems.map((item, index) => {
              if (item.type === 'link') {
                return (
                  <li key={index} className={item.className || ''}>
                    {renderMenuLink(item)}
                  </li>
                );
              } else if (item.type === 'dropdown') {
                return (
                  <li key={index} className="nav-dropdown">
                    <button className="nav-dropdown-trigger" type="button">
                      {item.label}
                      <span className="nav-dropdown-arrow">▼</span>
                    </button>
                    <div className="nav-dropdown-menu">
                      {item.items.map((child, childIndex) => (
                        <div className="nav-dropdown-item" key={`${index}-${childIndex}`}>
                          {renderMenuLink(child)}
                        </div>
                      ))}
                    </div>
                  </li>
                );
              } else if (item.type === 'button') {
                return (
                  <li key={index}>
                    <button
                      onClick={() => {
                        item.onClick();
                      }}
                      className="nav-menu-button"
                    >
                      {item.label}
                    </button>
                  </li>
                );
              } else if (item.type === 'custom') {
                return (
                  <li key={index}>
                    {item.component}
                  </li>
                );
              }
              return null;
            })}
        </ul>
        {}
        <button
          className={`burger-menu ${isMenuOpen ? 'open' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
      {}
      <div className={`mobile-menu-overlay ${isMenuOpen ? 'open' : ''}`} onClick={closeMenu}>
        <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
          <div className="mobile-menu-header">
            <div className="nav-logo">{logo}</div>
            <button className="mobile-menu-close" onClick={closeMenu} aria-label="Close menu">
              ×
            </button>
          </div>
          <ul className="mobile-menu-list">
            {normalizedMenuItems.map((item, index) => {
              if (item.type === 'link') {
                return (
                  <li key={index}>
                    {renderMenuLink({ ...item, iconOnly: false }, true)}
                  </li>
                );
              } else if (item.type === 'dropdown') {
                return (
                  <li key={index} className="mobile-menu-group">
                    <div className="mobile-menu-group-label">{item.label}</div>
                    {item.items.map((child, childIndex) => (
                      <div key={`${index}-${childIndex}`} className="mobile-menu-subitem">
                        {renderMenuLink(child, true)}
                      </div>
                    ))}
                  </li>
                );
              } else if (item.type === 'button') {
                return (
                  <li key={index}>
                    <button
                      onClick={() => {
                        item.onClick();
                        closeMenu();
                      }}
                      className="mobile-menu-button"
                    >
                      {item.label}
                    </button>

                  </li>

                );
              } else if (item.type === 'custom') {
                return (
                  <li key={index}>
                    {item.component}
                  </li>

                );
              }
              return null;
            })}
          </ul>

        </div>

      </div>

    </nav>

  );
};

export default ResponsiveNavbar;
